/*
 * OrbitThinking — the LIVE turn indicator shown in the chat thread while
 * Orbit is processing the user's input, streamed over SSE.
 *
 * It evolves through the turn instead of being a static spinner:
 *   1. breathing dots                 — the agent is thinking
 *   2. ✓ "here's what I understood"   — the SSE `understood` line, the instant
 *                                        the agent reports back (seconds before
 *                                        the full turn settles)
 *   3. reply typing in, word by word  — the SSE `reply` chunks
 * On `done` the whole indicator is replaced by the committed Orbit turn.
 *
 * Brand-true: serif italic "Orbit" byline matching regular turns, breathing
 * dots, a yellow-accent check on the understood line, and a soft caret on the
 * streaming reply.
 */

import React from 'react';


export interface OrbitThinkingProps {
  /** Generic "what Orbit is working on" label (section-derived) shown only
   *  before the live stream produces anything (e.g. "Scoping your problem"). */
  status?: string | null;
  /** The streamed "here's what I understood" line (SSE `understood`). Shown
   *  with a check the instant the agent reports back. */
  understood?: string | null;
  /** The reply text, streamed in word-group by word-group (SSE `reply`).
   *  Replaced by the committed Orbit turn on `done`. */
  reply?: string | null;
}

export function OrbitThinking({ status, understood, reply }: OrbitThinkingProps = {}) {
  const hasReply = !!(reply && reply.trim());
  return (
    <div className="aa-turn aa-turn--orbit aa-turn--thinking" aria-live="polite">
      <div className="aa-turn-byline">
        <span>Orbit</span>
        {!hasReply && (
          <span className="aa-thinking-dots" aria-label="thinking">
            <span className="aa-thinking-dot" />
            <span className="aa-thinking-dot" />
            <span className="aa-thinking-dot" />
          </span>
        )}
      </div>

      {understood && (
        <div className="aa-thinking-understood">
          <span className="aa-thinking-check" aria-hidden="true">✓</span>
          <span>{understood}</span>
        </div>
      )}

      {hasReply ? (
        <div className="aa-thinking-reply">
          {reply}
          <span className="aa-thinking-cursor" aria-hidden="true" />
        </div>
      ) : (
        !understood && status && <div className="aa-thinking-status">{status}</div>
      )}
    </div>
  );
}

export default OrbitThinking;
